import { useMemo } from 'react';
import type { ResultEvent } from '../types/events';

export type TimerProps = {
  timingProvider: ResultEvent['provider'];
  listingSettings: ResultEvent['listingSettings'];
  relatedEvents: ResultEvent['relatedEvents'];
} | null;

export type UseTimerPropsReturn = {
  shouldShowTimerDetails: boolean;
  timerProps: TimerProps;
};

/**
 * Custom hook that extracts timer-related logic from event data
 * @param event - The current event (ResultEvent or null)
 * @returns Object containing timer visibility flag and prepared props
 */
export const useTimerProps = (event: ResultEvent | null): UseTimerPropsReturn => {
  return useMemo(() => {
    // Early return for no event
    if (!event) {
      return {
        shouldShowTimerDetails: false,
        timerProps: null,
      };
    }

    const { provider: timingProvider, listingSettings, relatedEvents = [] } = event;

    // Check if we have timer details to display
    const hasTimingProvider = Boolean(timingProvider);
    const hasTimerContactForm = Boolean(listingSettings?.hasTimerContactForm);

    // Show timer details if we have a timing provider OR if contact form is enabled
    const shouldShowTimerDetails = hasTimingProvider || hasTimerContactForm;

    // Build timer props only if we should show timer details
    const timerProps = shouldShowTimerDetails
      ? {
          timingProvider,
          listingSettings,
          relatedEvents,
        }
      : null;

    return {
      shouldShowTimerDetails,
      timerProps,
    };
  }, [event]);
};
